import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface TooltipsProps {
  id: number;
}

type citationKey = keyof typeof citations;

export const Tooltips: React.FC<TooltipsProps> = ({ id }) => {
  if (citations[id as citationKey] === undefined) {
    return (
      <OverlayTrigger
        placement="top"
        show={true}
        overlay={
          <Tooltip id={`citation-${id}`} className="font-weight-bold">
            Check citation
          </Tooltip>
        }
      >
        <sup
          aria-describedby={`citation-${id}`}
          className="text-danger bg-gold font-weight-bold"
        >
          ???
        </sup>
      </OverlayTrigger>
    );
  }
  return (
    <OverlayTrigger
      trigger={["hover", "focus"]}
      placement="top"
      delay={{ show: 50, hide: 1500 }}
      overlay={
        <Tooltip id={`citation-${id}`}>{citations[id as citationKey]}</Tooltip>
      }
    >
      <sup aria-describedby={`citation-${id}`}>{id}</sup>
    </OverlayTrigger>
  );
};

const citations = {
  1: (
    <cite>
      World Health Organization. (2021). Occupational health: Workplace health
      promotion. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="http://www.who.int/occupational_health/topics/workplace/en"
      >
        http://www.who.int/occupational_health/topics/workplace/en
      </a>
    </cite>
  ),
  2: (
    <cite>
      Australian Institute of Health and Welfare. (2012). Risk factors
      contributing to chronic disease. Cat No. PHE 157. Canberra: AIHW
    </cite>
  ),
  3: (
    <cite>
      Australian Institute of Health and Welfare. (2014). Australia’s health
      2014: in brief. Cat. No. AUS 181. Canberra: AIHW.
    </cite>
  ),
  4: (
    <cite>
      Health and Productivity Institute of Australia. (2010). Best Practice
      Guidelines – Workplace. Health in Australia. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.healthierwork.act.gov.au/supporting-resources/hapiabestpracticeguidelines"
      >
        https://www.healthierwork.act.gov.au/supporting-resources/hapiabestpracticeguidelines
      </a>
    </cite>
  ),
  5: (
    <cite>
      Australian Bureau of Statistics. (2013). Australian Health Survey:
      Physical Activity. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.abs.gov.au/statistics/health/health-conditions-and-risks/australian-health-survey-physical-activity/latest-release"
      >
        https://www.abs.gov.au/statistics/health/health-conditions-and-risks/australian-health-survey-physical-activity/latest-release
      </a>
    </cite>
  ),
  6: (
    <cite>
      World Health Organization. (2010). Global Recommendations on Physical
      Activity for Health. World Health Organization, Geneva.
    </cite>
  ),
  7: (
    <cite>
      Begg, S., Vos, T., Barker, B., Stevenson, C., Stanley, L. &amp; Lopez,
      A.D. (2007). The burden of disease and injury in Australia 2003. PHE 82.
      Canberra: AIHW.
    </cite>
  ),
  8: (
    <cite>
      Australian Institute of Health and Welfare. (2020). Australia’s health
      2020: in brief. Australia’s health series no. 17 Cat. no. AUS 232.
      Canberra: AIHW.
    </cite>
  ),
  9: (
    <cite>
      National Health and Medical Research Council. (2013). Australian Dietary
      Guidelines: Providing the scientific evidence for healthier Australian
      diets. Canberra: National Health and Medical Research Council.
    </cite>
  ),
  10: (
    <cite>
      Australian Bureau of Statistics. (2018). National Health Survey: First
      results. 4364.0.55.001 Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.abs.gov.au/statistics/health/health-conditions-and-risks/national-health-survey-first-results/latest-release"
      >
        https://www.abs.gov.au/statistics/health/health-conditions-and-risks/national-health-survey-first-results/latest-release
      </a>
    </cite>
  ),
  11: (
    <cite>
      Australian Bureau of Statistics. (2014). Australian Health Survey:
      nutrition first results—foods and nutrients, 2011–12. ABS cat. no.
      4364.0.55.007. Canberra: ABS. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.abs.gov.au/statistics/health/health-conditions-and-risks/australian-health-survey-nutrition-first-results-foods-and-nutrients/latest-release"
      >
        https://www.abs.gov.au/statistics/health/health-conditions-and-risks/australian-health-survey-nutrition-first-results-foods-and-nutrients/latest-release
      </a>
    </cite>
  ),
  12: (
    <cite>
      Australian Bureau of Statistics. (2018). High Cholesterol. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="https://www.abs.gov.au/statistics/health/health-conditions-and-risks/high-cholesterol/latest-release#:~:text=In%202017%2D18%2C%206.1%25,had%20high%20cholesterol%20(6.1%25)"
      >
        https://www.abs.gov.au/statistics/health/health-conditions-and-risks/high-cholesterol/latest-release#:~:text=In%202017%2D18%2C%206.1%25,had%20high%20cholesterol%20(6.1%25)
      </a>
    </cite>
  ),
  13: (
    <cite>
      Cancer Council Western Australia. (2021). Smoking and tobacco. Retrieved
      from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="http://www.cancerwa.asn.au/prevention/tobacco"
      >
        http://www.cancerwa.asn.au/prevention/tobacco
      </a>
      ;
    </cite>
  ),
  14: (
    <cite>
      Epidemiology Branch. (2020). Prevalence of Smokers and E-cigarette Users,
      18 years and over, 2002-2019. WA Department of Health: Perth
    </cite>
  ),
  15: (
    <cite>
      Epidemiology Branch. (2018). Western Australia tobacco attributable
      related hospitalisation and deaths in 2017. WA Department of Health: Perth
    </cite>
  ),
  16: (
    <cite>
      Tait, R.J. &amp; Allsop, S. (2019). Identifying the social costs of
      tobacco use of Australia in 2015/16. National Drug Research Institute:
      Perth
    </cite>
  ),
  17: (
    <cite>
      National Health and Medical Research Council, Australian Research Council
      and Universities Australia. (2020). Australian guidelines to reduce health
      risks from drinking alcohol. Canberra: National Health and Medical
      Research Council.
    </cite>
  ),
  18: (
    <cite>
      Government of Western Australia Drug and Alcohol Office. (2014). Alcohol
      think again: Alcohol and your long-term health. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="http://alcoholthinkagain.com.au/Alcohol-Your-Health/Alcohol-and-Your-Long-Term-Health"
      >
        http://alcoholthinkagain.com.au/Alcohol-Your-Health/Alcohol-and-Your-Long-Term-Health
      </a>
    </cite>
  ),
  19: (
    <cite>
      VicHealth. (2012). Reducing alcohol-related harm in the workplace. An
      evidence review: summary report. Victorian Health Promotion Foundation,
      Melbourne, Australia.
    </cite>
  ),
  20: (
    <cite>
      Medibank Private. (2005). The health of Australia’s workforce, Medibank
      Private, Australia.
    </cite>
  ),
  21: (
    <cite>
      Goetzel, R. Z., &amp; Ozminkowski, R. J. (2008). The health and cost
      benefits of work site health promotion programs. Annual Review of Public
      Health. 29, 303-323.
    </cite>
  ),
  22: (
    <cite>
      Hymel, P. A., Loeppke, R. R., Baase, C. M., Burton, W. N., Hartenbaum, N.
      P., Hudson, T. W., &amp; Larson, P. W. (2011). Workplace health protection
      and promotion: a new pathway for a healthier—and safer—workforce. Journal
      of Occupational and Environmental Medicine. 53(6), 695-702.
    </cite>
  ),
  23: (
    <cite>
      Dunstan, D.W., Howard, B., Healy, G.N. &amp; Owen, N. (2012). Too much
      sitting – A health hazard. Diabetes Research and Clinical Practice.
      97:368-376.
    </cite>
  ),
  24: (
    <cite>
      Centers for Disease Control and Prevention. (2013). Workplace health
      promotion. Retrieved from{" "}
      <a
        target="_blank"
        rel="noopener noreferrer"
        href="http://www.cdc.gov/workplacehealthpromotion/index.html"
      >
        http://www.cdc.gov/workplacehealthpromotion/index.html
      </a>
    </cite>
  ),
  25: (
    <cite>
      Heath. C. &amp; Health, D. (2007). Made to stick: Why some ideas survive and others die (2nd ed.). New York: Random House.
    </cite>
  ),
  26: (
    <cite>
      Prochaska, J.O. &amp; DiClemente, C.C. (1982) Transtheoretical therapy: Toward a more integrative model of change. Pcychotherapy: Theory, Research and Practice. 19: 276-288.
    </cite>
  ),
};
